import { useQuery } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAdminPartnerCodes } from "@/hooks/useAdminPartnerCodes";
import type { Tables } from "@/integrations/supabase/types";

type PartnerCode = Tables<"partner_codes">;

interface DeletePartnerCodeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partnerCode: PartnerCode;
}

const DeletePartnerCodeDialog = ({
  open,
  onOpenChange,
  partnerCode,
}: DeletePartnerCodeDialogProps) => {
  const { deletePartnerCode, isDeleting } = useAdminPartnerCodes();

  const { data: redemptionCount } = useQuery({
    queryKey: ["partner-code-redemptions", partnerCode.id],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("partner_code_redemptions")
        .select("*", { count: "exact", head: true })
        .eq("partner_code_id", partnerCode.id);

      if (error) throw error;
      return count || 0;
    },
    enabled: open,
  });

  const handleDelete = () => {
    deletePartnerCode(partnerCode.id, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Partner Code</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete the code <strong>{partnerCode.code}</strong>?
            {redemptionCount !== undefined && redemptionCount > 0 && (
              <span className="block mt-2 text-destructive font-semibold">
                Warning: This will permanently delete the code and all {redemptionCount} redemption
                {redemptionCount !== 1 ? " records" : " record"}. This action cannot be undone.
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleDelete} 
            disabled={isDeleting}
            className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-600"
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeletePartnerCodeDialog;
